import type { MatchType, SegmentMember, SegmentRule } from "#shared/segmentsTypes.ts";
import { Disclosure, DisclosurePanel, DisclosureTrigger } from "@/components/ui/disclosure-group.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo, useState, type UIEvent } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { PersonListItem } from "@packages/core-client/people/PersonListItem.tsx";
import { RefreshErrorBanner } from "@packages/core-client/collection/RefreshErrorBanner.tsx";
import { useInfiniteCollection } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";

const PREVIEW_DEBOUNCE_MS = 300;
const PREVIEW_PAGE_SIZE = 50;
const LOAD_MORE_THRESHOLD_PX = 100;

const PLACEHOLDER_MEMBERS: SegmentMember[] = [
    { id: "skeleton-1", displayName: "Xxxxx Xxxxxx" },
    { id: "skeleton-2", displayName: "Xxxxxxxx Xxxx" }
];

export interface SegmentMemberPreviewProps {
    matchType: MatchType;
    rules: SegmentRule[];
}

export function SegmentMemberPreview({ matchType, rules }: SegmentMemberPreviewProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("SegmentMemberPreview", { i18n: i18nextInstance });
    const { client, queryUtils } = useSegmentsClient();
    const session = useSession();
    const orgId = session?.organizationId ?? "";

    const debouncedMatchType = useDebouncedValue(matchType, PREVIEW_DEBOUNCE_MS);
    const debouncedRules = useDebouncedValue(rules, PREVIEW_DEBOUNCE_MS);

    const previewInput = useMemo(() => ({ orgId, matchType: debouncedMatchType, rules: debouncedRules, pageSize: PREVIEW_PAGE_SIZE }), [orgId, debouncedMatchType, debouncedRules]);

    const {
        items: members,
        totalCount,
        isLoading,
        isRefreshing,
        isColdError,
        isStaleError,
        hasNextPage,
        isFetchingNextPage,
        loadMore,
        refetch,
        loadedAnnouncement,
        refreshedAnnouncement
    } = useInfiniteCollection<SegmentMember>({
        queryKey: [...queryUtils.previewSegmentMembers.key(), previewInput],
        queryFn: continuationToken => client.previewSegmentMembers({ ...previewInput, continuationToken }),
        placeholder: PLACEHOLDER_MEMBERS,
        hasActiveFilters: true,
        getLoadedMoreMessage: count => t("liveRegion.loadedMore", { count }),
        getRefreshedMessage: count => t("liveRegion.refreshed", { count }),
        onError: "inline"
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const handleRetry = useCallback(() => refetch(), [refetch]);

    const handleScroll = useCallback(
        (event: UIEvent<HTMLUListElement>) => {
            if (!hasNextPage || isFetchingNextPage) {
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

            if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX) {
                loadMore();
            }
        },
        [hasNextPage, isFetchingNextPage, loadMore]
    );

    // intent-ui-deviation: hand-rolled chrome rather than Intent UI's CardFooter -- CardFooter forces
    const bandClassName = "rounded-b-lg border-t bg-secondary/30 px-(--gutter) pt-4 pb-4 -mb-(--gutter)";

    const liveRegions = (
        <>
            <output className="sr-only">{isColdError ? t("error") : isLoading || isRefreshing ? t("loading") : ""}</output>
            <output className="sr-only">{loadedAnnouncement}</output>
            <output className="sr-only">{refreshedAnnouncement}</output>
        </>
    );

    if (isColdError) {
        return (
            <div className={bandClassName}>
                {liveRegions}
                <ErrorState isEmbedded kind="load" title={t("error")} onRetry={handleRetry} retryLabel={t("retry")} />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={bandClassName}>
                {liveRegions}
                <Skeleton isLoading inert className="flex">
                    <div className="h-4 w-40" />
                </Skeleton>
            </div>
        );
    }

    if (totalCount === undefined) {
        return null;
    }

    const matchSuffix = totalCount === 0 ? undefined : t("matchSuffix", { count: totalCount });

    if (totalCount === 0) {
        return (
            <div className={`${bandClassName} flex flex-col gap-1`}>
                {liveRegions}
                <p className="text-fg text-sm font-medium">{t("empty")}</p>
            </div>
        );
    }

    return (
        <div className={bandClassName}>
            {liveRegions}
            {isStaleError && <RefreshErrorBanner message={t("staleError")} className="mb-2" />}
            <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
                <DisclosureTrigger triggerIndicator={false} name="member-preview-toggle" className="justify-start gap-2 py-1">
                    {isExpanded ? <ChevronUpIcon aria-hidden="true" className="size-4" /> : <ChevronDownIcon aria-hidden="true" className="size-4" />}
                    <span className="flex flex-col items-start gap-0.5">
                        <span className="text-sm">
                            <span className="text-fg font-semibold">{totalCount}</span> <span className="text-muted-fg">{matchSuffix}</span>
                        </span>
                        {isRefreshing && (
                            <span aria-hidden="true" className="text-muted-fg text-xs font-normal">
                                {t("loading")}
                            </span>
                        )}
                    </span>
                </DisclosureTrigger>
                <DisclosurePanel>
                    <ul className="grid max-h-80 grid-cols-2 gap-x-6 gap-y-2 overflow-y-auto" onScroll={handleScroll}>
                        {members.map(member => (
                            <li key={member.id}>
                                <PersonListItem personId={member.id} displayName={member.displayName} secondaryLabel={member.jobTitle} imageUrl={member.imageUrl} />
                            </li>
                        ))}
                    </ul>
                </DisclosurePanel>
            </Disclosure>
        </div>
    );
}
